import React, { useEffect, useMemo, useRef, useState } from 'react';
import Split from 'react-split';
import './WarehouseConfig.css';
import { FaEdit, FaTrash, FaSave, FaTimes, FaColumns, FaBarcode, FaPrint, FaTag, FaSync, FaPlus, FaWarehouse } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const emptyForm = { name: '', address: '', city: '', state: '', zip: '', length: 2000, width: 1200, height: 32 };

export default function WarehouseConfig() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [selectedWh, setSelectedWh] = useState(null);
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState('');
  const [aisles, setAisles] = useState([]);
  const [aislesLoading, setAislesLoading] = useState(false);
  const [selectedAisle, setSelectedAisle] = useState('');
  const [locs, setLocs] = useState([]);
  const [locsLoading, setLocsLoading] = useState(false);
  // Map of locationid -> storage unit name (from pallets currently at that location)
  const [locStorageMap, setLocStorageMap] = useState({});
  // Map of locationid -> formatted SKU/Qty string (primary SKU × quantity)
  const [locSkuQtyMap, setLocSkuQtyMap] = useState({});
  // Map of locationid -> stacks present (letters A,B,...) or primary stack letter
  const [locStacksMap, setLocStacksMap] = useState({});
  // Map of locationid -> storage unit id
  const [locStorageIdMap, setLocStorageIdMap] = useState({});
  // Map of locationid -> has inventory (boolean)
  const [locHasInvMap, setLocHasInvMap] = useState({});
  // All storage unit options (whstorageunit)
  const [storageUnits, setStorageUnits] = useState([]);
  const [selectedLocIds, setSelectedLocIds] = useState(() => new Set());
  const [lastLocAnchorIndex, setLastLocAnchorIndex] = useState(null);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [pendingType, setPendingType] = useState('2');
  const [typeSaving, setTypeSaving] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printTargets, setPrintTargets] = useState([]);
  const [labelSize, setLabelSize] = useState('4x2');
  const [newBayOpen, setNewBayOpen] = useState(false);
  const [bayForm, setBayForm] = useState({ bay: '', shelves: 1, slots: 1, type: '2' });
  const aislesListRef = useRef(null);
  const locTypeThRef = useRef(null);
  const [typeBtnPos, setTypeBtnPos] = useState({ left: 0, width: 0 });
  const [aisleForm, setAisleForm] = useState({ aisle: '', bays: 1, shelves: 1, slots: 1, type: '2' });
  const [creatingAisle, setCreatingAisle] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [newAisleOpen, setNewAisleOpen] = useState(false);
  const [splitSizes, setSplitSizes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('whcfg-split-sizes') || '[60,40]');
    } catch {
      return [60, 40];
    }
  });
  const selectAllRef = useRef(null);

  // Resizable columns
  const WHCFG_COLS = ['actions', 'id', 'name', 'address', 'city', 'state', 'zip', 'length', 'width', 'height'];
  const LS_WHC_COLS = 'whcfgColWidths.v1';
  const getInitialWhcWidths = () => {
    try {
      const raw = localStorage.getItem(LS_WHC_COLS);
      const j = raw ? JSON.parse(raw) : {};
      return (j && typeof j === 'object') ? j : {};
    } catch {
      return {};
    }
  };
  const [whcColWidths, setWhcColWidths] = useState(getInitialWhcWidths);
  const DEFAULT_WHC_WIDTHS = useMemo(() => ({
    id: 60,
    name: 180,
    address: 220,
    city: 140,
    state: 80,
    zip: 110,
    length: 100,
    width: 100,
    height: 100,
  }), []);
  useEffect(() => {
    try {
      localStorage.setItem(LS_WHC_COLS, JSON.stringify(whcColWidths));
    } catch {}
  }, [whcColWidths]);

  const whcHeaderRef = useRef(null);
  const whcTableRef = useRef(null);
  const whcResizeState = useRef({ key: null, startX: 0, startW: 0 });
  const MIN_COL_W = 40;

  const ensureWhcWidthsLocked = () => {
    const row = whcHeaderRef.current;
    if (!row) return;
    const ths = Array.from(row.children || []);
    if (!ths.length) return;
    const haveAll = WHCFG_COLS.every(k => whcColWidths && whcColWidths[k] != null);
    if (haveAll) return;
    const next = {};
    WHCFG_COLS.forEach((k, i) => {
      const th = ths[i];
      if (th) next[k] = th.offsetWidth;
    });
    setWhcColWidths(prev => ({ ...next, ...prev }));
  };

  const onWhcMove = (e) => {
    const { key, startX, startW } = whcResizeState.current;
    if (!key) return;
    const nextW = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setWhcColWidths(prev => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };

  const onWhcUp = () => {
    whcResizeState.current = { key: null, startX: 0, startW: 0 };
    try {
      document.body.classList.remove('col-resizing');
    } catch {}
    window.removeEventListener('mousemove', onWhcMove);
    window.removeEventListener('mouseup', onWhcUp);
  };

  const startWhcResize = (key, e) => {
    ensureWhcWidthsLocked();
    const th = e.currentTarget.parentElement;
    whcResizeState.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try {
      document.body.classList.add('col-resizing');
    } catch {}
    window.addEventListener('mousemove', onWhcMove);
    window.addEventListener('mouseup', onWhcUp);
    e.preventDefault();
    e.stopPropagation();
  };

  const autoFitWhcCol = (key) => {
    try {
      const idx = WHCFG_COLS.indexOf(key);
      if (idx === -1) return;
      let maxW = 0;
      if (whcHeaderRef.current?.children[idx]) {
        maxW = Math.max(maxW, Math.ceil(whcHeaderRef.current.children[idx].scrollWidth));
      }
      const tbl = whcTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const w = Math.max(MIN_COL_W, Math.min(800, maxW));
      setWhcColWidths(prev => ({ ...prev, [key]: w }));
    } catch {}
  };

  const widthFor = (key, isLast) => {
    if (isLast) return undefined;
    const w = whcColWidths[key];
    const d = DEFAULT_WHC_WIDTHS[key];
    const px = (w != null ? w : d);
    return px != null ? `${px}px` : undefined;
  };

  const resetWhcColumns = () => {
    try {
      localStorage.removeItem(LS_WHC_COLS);
    } catch {}
    setWhcColWidths({});
    setTimeout(() => ensureWhcWidthsLocked(), 0);
  };

  useEffect(() => {
    const t = setTimeout(() => ensureWhcWidthsLocked(), 0);
    return () => clearTimeout(t);
  }, [rows.length]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/warehouses`);
      const j = await r.json();
      setRows(Array.isArray(j) ? j : []);
    } catch (e) {
      setRows([]);
      setError('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', { detail: { title: 'Warehouse Config', subtitle: '' } }));
    return () => window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  }, []);

  useEffect(() => {
    load();
  }, []);

  // Load storage units (whstorageunit table)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/packagingunits/storageunits`);
        const j = await r.json();
        const arr = Array.isArray(j) ? j : [];
        setStorageUnits(arr);
      } catch {
        setStorageUnits([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/locations/types`);
        const j = await res.json();
        const list = Array.isArray(j) ? j : [];
        setLocationTypes(list);
        const nonFloor = list.find(t => String(t.id) !== '1');
        setAisleForm(f => ({ ...f, type: String(f.type || (nonFloor?.id ?? '2')) }));
      } catch {
        setLocationTypes([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedWh) {
      setAreas([]);
      setAreaId('');
      setAisles([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/warehouseareas/warehouseareas?warehouseid=${encodeURIComponent(selectedWh.id)}`);
        const j = await res.json();
        const list = Array.isArray(j) ? j : [];
        setAreas(list);
        const firstId = list[0]?.id ? String(list[0].id) : '';
        setAreaId(prev => prev || firstId);
      } catch {
        setAreas([]);
        setAreaId('');
      }
    })();
  }, [selectedWh]);

  useEffect(() => {
    if (!selectedWh || !areaId) {
      setAisles([]);
      return;
    }
    (async () => {
      setAislesLoading(true);
      try {
        const url = `${API}/api/locations/aisles?warehouseid=${encodeURIComponent(selectedWh.id)}&areaid=${encodeURIComponent(areaId)}`;
        const res = await fetch(url);
        const j = await res.json();
        const list = Array.isArray(j) ? j : [];
        setAisles(list);
      } catch {
        setAisles([]);
      } finally {
        setAislesLoading(false);
      }
    })();
  }, [selectedWh?.id, areaId]);

  useEffect(() => {
    if (!Array.isArray(aisles) || aisles.length === 0) {
      setSelectedAisle('');
      return;
    }
    setSelectedAisle(prev => {
      if (prev && aisles.some(a => String(a.roww) === String(prev))) return prev;
      const first = aisles[0]?.roww;
      return first != null ? String(first) : '';
    });
  }, [aisles]);

  useEffect(() => {
    if (!selectedWh || !areaId) {
      setLocs([]);
      setLocStorageMap({});
      setLocSkuQtyMap({});
      return;
    }
    (async () => {
      setLocsLoading(true);
      try {
        const url = `${API}/api/locations?warehouseid=${encodeURIComponent(selectedWh.id)}&areaid=${encodeURIComponent(areaId)}`;
        const res = await fetch(url);
        const j = await res.json();
        const list = Array.isArray(j) ? j : [];
        list.sort((a, b) =>
          (Number(a.roww) - Number(b.roww)) ||
          (Number(a.columnn) - Number(b.columnn)) ||
          (Number(a.shelve) - Number(b.shelve)) ||
          (Number(a.bin) - Number(b.bin))
        );
        setLocs(list);
      } catch {
        setLocs([]);
      } finally {
        setLocsLoading(false);
      }
    })();
  }, [selectedWh?.id, areaId]);

  // Format SKU × Qty (similar to PickingReplenishment)
  const formatSkuQty = (r) => {
    try {
      const skusStr = (r?.skus || '').toString().trim();
      let skuPiecesStr = (r?.sku_pieces || '').toString().trim();
      const primarySku = skusStr ? skusStr.split(',')[0].trim() : '';
      let piecesMap = new Map();
      if (skuPiecesStr) {
        skuPiecesStr.split('|').forEach(pair => {
          const [s, q] = String(pair || '').split(':');
          const n = Number(q);
          if (s && Number.isFinite(n)) piecesMap.set(s.trim(), n);
        });
      }
      // Fallback: if sku_pieces is empty, try sku_qtys
      if (!piecesMap.size) {
        const skuQtysStr = (r?.sku_qtys || '').toString().trim();
        if (skuQtysStr) {
          skuQtysStr.split('|').forEach(pair => {
            const [s, q] = String(pair || '').split(':');
            const n = Number(q);
            if (s && Number.isFinite(n)) piecesMap.set(s.trim(), n);
          });
        }
      }
      const qty = primarySku && piecesMap.has(primarySku)
        ? piecesMap.get(primarySku)
        : (piecesMap.size ? Array.from(piecesMap.values())[0] : null);
      if (primarySku && qty != null) return `${primarySku} × ${qty}`;
      if (primarySku) return primarySku;
      if (qty != null) return `Qty ${qty}`;
      return '';
    } catch { return ''; }
  };

  // Convert numeric stack list to letters (1->A, 2->B, ...)
  const formatStacksLetters = (stacksCsv, primary, hasInv = false) => {
    try {
      const toLetter = (s) => {
        if (s == null) return '';
        const str = String(s).trim();
        if (!str) return '';
        // Accept both numeric (1,2,3,4,...) and letter forms (A,B,C,D,...)
        const upper = str.toUpperCase();
        if (/^[A-Z]$/.test(upper)) return upper;
        const n = Number(str);
        if (!Number.isFinite(n)) return '';
        return n === 1 ? 'A' : n === 2 ? 'B' : n === 3 ? 'C' : n === 4 ? 'D' : String(n);
      };
      const stacks = String(stacksCsv || '').trim();
      if (stacks) {
        return stacks.split(',').map(s => toLetter(s.trim())).filter(Boolean).join(', ');
      }
      if (primary != null && primary !== '') {
        const t = toLetter(primary);
        if (t) return t;
      }
      // If location has inventory but no explicit stack value, default to 'A'
      if (hasInv) return 'A';
      return '';
    } catch { return ''; }
  };

  // Fetch storage unit names and SKU/Qty per location (like PickingReplenishment) for the selected aisle
  useEffect(() => {
    (async () => {
      try {
        if (!selectedWh || !areaId) { setLocStorageMap({}); setLocSkuQtyMap({}); setLocStorageIdMap({}); setLocHasInvMap({}); return; }
        const aisle = Number(selectedAisle);
        if (!Number.isFinite(aisle)) { setLocStorageMap({}); setLocSkuQtyMap({}); setLocStorageIdMap({}); setLocHasInvMap({}); return; }
        // Compute bay range from filteredLocs; fallback to a wide range
        const bays = (filteredLocs || []).map(r => Number(r.columnn)).filter(n => Number.isFinite(n));
        const bayStart = bays.length ? Math.min(...bays) : 1;
        const bayEnd   = bays.length ? Math.max(...bays) : bayStart;
        const qs = new URLSearchParams({
          warehouseid: String(selectedWh.id),
          areaid: String(areaId),
          aisleStart: String(aisle),
          aisleEnd: String(aisle),
          bayStart: String(bayStart),
          bayEnd: String(bayEnd)
        });
        const res = await fetch(`${API}/api/locations/range-with-pallets?${qs.toString()}`);
        const arr = await res.json();
        const map = {}, idMap = {}, skuMap = {}, hasMap = {}, stacksMap = {};
        if (Array.isArray(arr)) {
          for (const r of arr) {
            if (r && (r.locationid != null)) {
              map[r.locationid] = (r.storageunitname || '') || '';
              idMap[r.locationid] = r.storageunitid || null;
              skuMap[r.locationid] = formatSkuQty(r);
              const hasInv = Number(r.in_use || 0) > 0;
              hasMap[r.locationid] = hasInv;
              stacksMap[r.locationid] = formatStacksLetters(r.stacks, r.stack, hasInv);
            }
          }
        }
        setLocStorageMap(map);
        setLocStorageIdMap(idMap);
        setLocSkuQtyMap(skuMap);
        setLocHasInvMap(hasMap);
        setLocStacksMap(stacksMap);
      } catch {
        setLocStorageMap({});
        setLocStorageIdMap({});
        setLocSkuQtyMap({});
        setLocHasInvMap({});
        setLocStacksMap({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWh?.id, areaId, selectedAisle, locs.length]);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm(emptyForm);
  };

  const cancelCreate = () => {
    setCreating(false);
    setForm(emptyForm);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setCreating(false);
    setForm({
      name: row.name || '',
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      zip: row.zip || '',
      length: Number(row.length || 2000),
      width: Number(row.width || 1200),
      height: Number(row.height || 32)
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const saveCreate = async () => {
    try {
      const res = await fetch(`${API}/api/warehouses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j.error || 'Create failed');
      setCreating(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const saveEdit = async (id) => {
    try {
      const res = await fetch(`${API}/api/warehouses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j.error || 'Update failed');
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const softDelete = async (id) => {
    if (!window.confirm('Delete this warehouse?')) return;
    try {
      await fetch(`${API}/api/warehouses/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const onF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const onAisleF = (k) => (e) => setAisleForm(f => ({ ...f, [k]: e.target.value }));

  const maxAisle = useMemo(() => {
    return aisles.reduce((m, r) => Math.max(m, Number(r.roww) || 0), 0);
  }, [aisles]);
  const nextAisle = (maxAisle || 0) + 1;

  const refreshAisles = async () => {
    if (!selectedWh || !areaId) return;
    setAislesLoading(true);
    try {
      const prevAisle = selectedAisle;
      const aislesUrl = `${API}/api/locations/aisles?warehouseid=${encodeURIComponent(selectedWh.id)}&areaid=${encodeURIComponent(areaId)}`;
      const resA = await fetch(aislesUrl);
      const jA = await resA.json();
      const listA = Array.isArray(jA) ? jA : [];
      setAisles(listA);
      if (listA.length) {
        const hasPrev = prevAisle && listA.some(a => String(a.roww) === String(prevAisle));
        setSelectedAisle(hasPrev ? String(prevAisle) : String(listA[0].roww));
      } else {
        setSelectedAisle('');
      }
    } catch {
      setAisles([]);
    } finally {
      setAislesLoading(false);
    }
    setLocsLoading(true);
    try {
      const locsUrl = `${API}/api/locations?warehouseid=${encodeURIComponent(selectedWh.id)}&areaid=${encodeURIComponent(areaId)}`;
      const resL = await fetch(locsUrl);
      const jL = await resL.json();
      const listL = Array.isArray(jL) ? jL : [];
      listL.sort((a, b) =>
        (Number(a.roww) - Number(b.roww)) ||
        (Number(a.columnn) - Number(b.columnn)) ||
        (Number(a.shelve) - Number(b.shelve)) ||
        (Number(a.bin) - Number(b.bin))
      );
      setLocs(listL);
    } catch {
      setLocs([]);
    } finally {
      setLocsLoading(false);
    }
  };

  const createAisle = async () => {
    if (!selectedWh || !areaId) {
      alert('Select a warehouse and area first.');
      return;
    }
    const bays = Math.max(1, parseInt(aisleForm.bays, 10) || 1);
    const shelves = Math.max(1, parseInt(aisleForm.shelves, 10) || 1);
    const slots = Math.max(1, parseInt(aisleForm.slots, 10) || 1);
    const type = String(aisleForm.type || '2');
    const aisleNum = Math.max(1, parseInt(aisleForm.aisle, 10) || nextAisle);
    setCreatingAisle(true);
    try {
      for (let c = 1; c <= bays; c++) {
        for (let s = 1; s <= shelves; s++) {
          for (let b = 1; b <= slots; b++) {
            const payload = {
              warehouseid: Number(selectedWh.id),
              areaid: Number(areaId),
              roww: aisleNum,
              columnn: c,
              shelve: s,
              bin: b,
              type: Number(type)
            };
            const res = await fetch(`${API}/api/locations`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            if (!res.ok && res.status !== 409) {
              let msg = `${res.status} ${res.statusText}`;
              try {
                const j = await res.json();
                if (j?.error) msg = j.error;
              } catch {}
              throw new Error(msg);
            }
          }
        }
      }
      setSelectedAisle(String(aisleNum));
      refreshAisles();
      alert(`Aisle ${aisleNum} created with ${bays} bays, ${shelves} shelves, ${slots} slots.`);
      return true;
    } catch (e) {
      alert('Failed to create aisle: ' + (e?.message || e));
      return false;
    } finally {
      setCreatingAisle(false);
    }
  };

  const filteredLocs = useMemo(() => {
    if (!selectedAisle) return [];
    return (locs || []).filter(r => String(r.roww) === String(selectedAisle));
  }, [locs, selectedAisle]);

  const getLocKey = (r) => r.locationid || `${r.warehouseid}-${r.areaid}-${r.roww}-${r.columnn}-${r.shelve}-${r.bin}`;

  const allSelected = useMemo(() => {
    const list = filteredLocs || [];
    if (!list.length) return false;
    for (const r of list) {
      if (!selectedLocIds.has(getLocKey(r))) return false;
    }
    return true;
  }, [filteredLocs, selectedLocIds]);

  const someSelected = useMemo(() => {
    return selectedLocIds.size > 0 && !allSelected;
  }, [selectedLocIds, allSelected]);

  const nextBayNumber = useMemo(() => {
    const nums = (filteredLocs || []).map(r => Number(r.columnn)).filter(n => Number.isFinite(n));
    return nums.length ? (Math.max(...nums) + 1) : 1;
  }, [filteredLocs]);

  const defaultsForShelvesSlots = useMemo(() => {
    const maxShelves = (filteredLocs || []).reduce((m, r) => Math.max(m, Number(r.shelve) || 0), 0) || 1;
    const maxSlots = (filteredLocs || []).reduce((m, r) => Math.max(m, Number(r.bin) || 0), 0) || 1;
    return { shelves: maxShelves, slots: maxSlots };
  }, [filteredLocs]);

  useEffect(() => {
    if (!Array.isArray(filteredLocs)) {
      setSelectedLocIds(new Set());
      return;
    }
    const valid = new Set(filteredLocs.map(getLocKey));
    setSelectedLocIds(prev => new Set([...prev].filter(k => valid.has(k))));
    if (lastLocAnchorIndex != null && (lastLocAnchorIndex < 0 || lastLocAnchorIndex >= filteredLocs.length)) {
      setLastLocAnchorIndex(null);
    }
  }, [filteredLocs.length]);

  const onLocRowClick = (row, idx, e) => {
    const key = getLocKey(row);
    const isMeta = e.metaKey || e.ctrlKey;
    const isShift = e.shiftKey;
    if (isShift && lastLocAnchorIndex != null) {
      const a = Math.min(lastLocAnchorIndex, idx);
      const b = Math.max(lastLocAnchorIndex, idx);
      const rangeKeys = new Set(filteredLocs.slice(a, b + 1).map(getLocKey));
      setSelectedLocIds(prev => {
        if (isMeta) {
          const next = new Set(prev);
          rangeKeys.forEach(k => next.add(k));
          return next;
        }
        return rangeKeys;
      });
    } else if (isMeta) {
      setSelectedLocIds(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      setLastLocAnchorIndex(idx);
    } else {
      setSelectedLocIds(new Set([key]));
      setLastLocAnchorIndex(idx);
    }
  };

  const openTypeModal = () => {
    const selectedRows = filteredLocs.filter(r => selectedLocIds.has(getLocKey(r)));
    const nonFloorTypes = (locationTypes || []).filter(t => String(t.id) !== '1');
    let next = '2';
    if (selectedRows.length === 1) {
      const t = String(selectedRows[0]?.type ?? '');
      if (t && t !== '1') next = t;
    } else if (selectedRows.length > 1) {
      const counts = {};
      for (const r of selectedRows) {
        const t = String(r?.type ?? '');
        if (!t || t === '1') continue;
        counts[t] = (counts[t] || 0) + 1;
      }
      let modeT = '';
      let modeC = -1;
      for (const [t, c] of Object.entries(counts)) {
        if (c > modeC) {
          modeC = c;
          modeT = t;
        }
      }
      if (modeT) next = modeT;
    }
    if (!nonFloorTypes.some(t => String(t.id) === String(next))) {
      next = String(nonFloorTypes[0]?.id || '2');
    }
    setPendingType(String(next));
    setTypeModalOpen(true);
  };

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected, filteredLocs.length]);

  const onToggleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const next = new Set((filteredLocs || []).map(getLocKey));
      setSelectedLocIds(next);
    } else {
      setSelectedLocIds(new Set());
    }
  };

  const onRowCheckboxChange = (row, e) => {
    e.stopPropagation();
    const key = getLocKey(row);
    const checked = e.target.checked;
    setSelectedLocIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const saveSelectedTypes = async () => {
    if (!selectedLocIds.size) {
      setTypeModalOpen(false);
      return;
    }
    setTypeSaving(true);
    const targetType = Number(pendingType);
    try {
      const selectedRows = filteredLocs.filter(r => selectedLocIds.has(getLocKey(r)) && r.locationid);
      const updates = selectedRows.map(async (r) => {
        const body = {
          warehouseid: r.warehouseid,
          areaid: r.areaid,
          roww: r.roww,
          columnn: r.columnn,
          shelve: r.shelve,
          bin: r.bin,
          type: targetType,
          img: r.img ?? null,
        };
        const res = await fetch(`${API}/api/locations/${encodeURIComponent(r.locationid)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          let msg = `${res.status} ${res.statusText}`;
          try {
            const j = await res.json();
            if (j?.error) msg = j.error;
          } catch {}
          throw new Error(msg);
        }
      });
      await Promise.all(updates);
      setTypeModalOpen(false);
      try {
        setLocsLoading(true);
        const url = `${API}/api/locations?warehouseid=${encodeURIComponent(selectedWh.id)}&areaid=${encodeURIComponent(areaId)}`;
        const r = await fetch(url);
        const j = await r.json();
        const list = Array.isArray(j) ? j : [];
        list.sort((a, b) =>
          (Number(a.roww) - Number(b.roww)) ||
          (Number(a.columnn) - Number(b.columnn)) ||
          (Number(a.shelve) - Number(b.shelve)) ||
          (Number(a.bin) - Number(b.bin))
        );
        setLocs(list);
      } finally {
        setLocsLoading(false);
      }
    } catch (e) {
      alert('Failed to update location types: ' + (e?.message || e));
    } finally {
      setTypeSaving(false);
    }
  };

  const typeLabelFor = (id) => {
    const s = String(id ?? '');
    const found = (locationTypes || []).find(t => String(t.id) === s);
    if (found && found.type) return found.type;
    if (s === '1') return 'Floor';
    if (s === '2') return 'Rack';
    if (s === '3') return 'Picking';
    return `Type ${s || '?'}`;
  };

  const printLocLabel = (r) => {
    const id = r?.locationid;
    if (!id) {
      alert('Missing location id for this row');
      return;
    }
    setPrintTargets([id]);
    setLabelSize('4x2');
    setPrintModalOpen(true);
  };

  const openPrintModalForSelection = () => {
    const ids = filteredLocs
      .filter(r => selectedLocIds.has(getLocKey(r)) && r.locationid)
      .map(r => r.locationid);
    if (!ids.length) {
      alert('Select one or more rows to print labels.');
      return;
    }
    setPrintTargets(ids);
    // If printing a large batch, default to OL125WX (10 labels per page)
    setLabelSize(ids.length >= 10 ? 'ol125wx' : '4x2');
    setPrintModalOpen(true);
  };

  const doPrint = () => {
    const ids = Array.isArray(printTargets) ? printTargets.filter(Boolean) : [];
    if (!ids.length) {
      setPrintModalOpen(false);
      return;
    }
    const url = `${API}/api/locations/labels?ids=${encodeURIComponent(ids.join(','))}&size=${encodeURIComponent(labelSize)}&sym=pdf417&v=${Date.now()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setPrintModalOpen(false);
  };

  // For OL125WX, show page count (10 labels per page)
  const ol125Pages = useMemo(() => {
    if (labelSize !== 'ol125wx') return 0;
    const n = Array.isArray(printTargets) ? printTargets.length : 0;
    return Math.ceil(n / 10);
  }, [labelSize, printTargets.length]);

  const openNewBayModal = () => {
    if (!selectedWh || !areaId || !selectedAisle) {
      alert('Select a warehouse, area, and aisle first.');
      return;
    }
    const nonFloor = (locationTypes || []).find(t => String(t.id) !== '1');
    setBayForm({
      bay: String(nextBayNumber),
      shelves: defaultsForShelvesSlots.shelves,
      slots: defaultsForShelvesSlots.slots,
      type: String(nonFloor?.id || '2'),
    });
    setNewBayOpen(true);
  };

  const onBayF = (k) => (e) => setBayForm(f => ({ ...f, [k]: e.target.value }));

  const createBay = async () => {
    if (!selectedWh || !areaId || !selectedAisle) {
      alert('Select a warehouse, area, and aisle first.');
      return;
    }
    const bayNum = Math.max(1, parseInt(bayForm.bay, 10) || nextBayNumber);
    const shelves = Math.max(1, parseInt(bayForm.shelves, 10) || 1);
    const slots = Math.max(1, parseInt(bayForm.slots, 10) || 1);
    const type = String(bayForm.type || '2');
    try {
      for (let s = 1; s <= shelves; s++) {
        for (let b = 1; b <= slots; b++) {
          const payload = {
            warehouseid: Number(selectedWh.id),
            areaid: Number(areaId),
            roww: Number(selectedAisle),
            columnn: bayNum,
            shelve: s,
            bin: b,
            type: Number(type)
          };
          const res = await fetch(`${API}/api/locations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!res.ok && res.status !== 409) {
            let msg = `${res.status} ${res.statusText}`;
            try {
              const j = await res.json();
              if (j?.error) msg = j.error;
            } catch {}
            throw new Error(msg);
          }
        }
      }
      await refreshAisles();
      setNewBayOpen(false);
    } catch (e) {
      alert('Failed to create bay: ' + (e?.message || e));
    }
  };

  const recomputeTypeBtnPos = () => {
    const cont = aislesListRef.current;
    const th = locTypeThRef.current;
    if (!cont || !th) return;
    try {
      const left = Math.max(0, th.offsetLeft - cont.scrollLeft);
      const width = th.offsetWidth || 140;
      setTypeBtnPos({ left, width });
    } catch {}
  };

  // Inline editing for location type
  const [inlineTypeEditing, setInlineTypeEditing] = useState(null); // locationid or null
  const [inlineTypeValue, setInlineTypeValue] = useState('2');
  const [inlineTypeSavingId, setInlineTypeSavingId] = useState(null);

  const saveInlineType = async (row, nextVal) => {
    try {
      setInlineTypeSavingId(row.locationid);
      // Backend PUT expects a full row payload; include existing fields
      const payload = {
        warehouseid: Number(row.warehouseid),
        areaid: Number(row.areaid),
        roww: Number(row.roww),
        columnn: Number(row.columnn),
        shelve: Number(row.shelve),
        bin: Number(row.bin),
        type: Number(nextVal),
        img: row.img ?? null,
      };
      const res = await fetch(`${API}/api/locations/${encodeURIComponent(row.locationid)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to update');
      // Update local list
      setLocs(prev => prev.map(r => (r.locationid === row.locationid ? { ...r, type: Number(nextVal) } : r)));
    } catch (e) {
      alert(e?.message || 'Failed to update location type');
    } finally {
      setInlineTypeSavingId(null);
      setInlineTypeEditing(null);
    }
  };

  // Inline editing for storage unit (when pallet present at location)
  const [inlineSUEditing, setInlineSUEditing] = useState(null); // locationid or null
  const [inlineSUValue, setInlineSUValue] = useState('');
  const [inlineSUSavingId, setInlineSUSavingId] = useState(null);
  // Delete locations modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const saveInlineStorageUnit = async (row, nextId) => {
    try {
      setInlineSUSavingId(row.locationid);
      const payload = { whstorageunitid: Number(nextId) };
      const res = await fetch(`${API}/api/locations/${encodeURIComponent(row.locationid)}/storageunit`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to update storage type');
      // Update local maps
      const su = storageUnits.find(s => String(s.id) === String(nextId));
      setLocStorageIdMap(prev => ({ ...prev, [row.locationid]: Number(nextId) }));
      setLocStorageMap(prev => ({ ...prev, [row.locationid]: (su?.name || '') }));
    } catch (e) {
      alert(e?.message || 'Failed to update storage type');
    } finally {
      setInlineSUSavingId(null);
      setInlineSUEditing(null);
    }
  };

  // When locations refresh (reload, filter change, or aisle change), exit any inline edit state
  useEffect(() => {
    setInlineTypeEditing(null);
    setInlineTypeSavingId(null);
    setInlineSUEditing(null);
    setInlineSUSavingId(null);
  }, [locs]);

  useEffect(() => {
    recomputeTypeBtnPos();
    const onResize = () => recomputeTypeBtnPos();
    window.addEventListener('resize', onResize);
    const cont = aislesListRef.current;
    const onScroll = () => recomputeTypeBtnPos();
    cont?.addEventListener('scroll', onScroll);
    const t = setTimeout(recomputeTypeBtnPos, 0);
    return () => {
      window.removeEventListener('resize', onResize);
      cont?.removeEventListener('scroll', onScroll);
      clearTimeout(t);
    };
  }, [filteredLocs.length, aisles.length, areaId, selectedWh?.id]);

  return (
    <div className="whcfg-wrap">
      <Split
        className="whcfg-split"
        direction="vertical"
        sizes={splitSizes}
        minSize={[200, 0]}
        gutterSize={6}
        onDragEnd={(sizes) => {
          setSplitSizes(sizes);
          try {
            localStorage.setItem('whcfg-split-sizes', JSON.stringify(sizes));
          } catch {}
        }}
      >
        <div className="whcfg-top-pane">
          <div className="whcfg-toolbar">
            <h2>Warehouse Config</h2>
            <div className="whcfg-actions">
              <button className="btn" onClick={load} disabled={loading}>
                <FaSync style={loading ? { marginRight: 6, verticalAlign: '-2px', animation: 'spin 1s linear infinite' } : { marginRight: 6, verticalAlign: '-2px' }} />
                {loading ? 'Loading…' : 'Refresh'}
              </button>
              <button className="btn" onClick={resetWhcColumns} title="Reset column widths">
                <FaColumns style={{ marginRight: 6 }} />
                Reset Columns
              </button>
              <button className="btn primary" onClick={startCreate}>
                <FaWarehouse style={{ marginRight: 6, verticalAlign: '-2px' }} />
                New Warehouse
              </button>
            </div>
          </div>
          {creating && (
            <div className="neo-card whcfg-card">
              <div className="form-grid whcfg-form-grid">
                <label>Name<input className="neo-input" value={form.name} onChange={onF('name')} /></label>
                <label>Address<input className="neo-input" value={form.address} onChange={onF('address')} /></label>
                <label>City<input className="neo-input" value={form.city} onChange={onF('city')} /></label>
                <label>State<input className="neo-input" value={form.state} onChange={onF('state')} /></label>
                <label>Zip<input className="neo-input" value={form.zip} onChange={onF('zip')} /></label>
                <label>Length (ft)<input type="number" className="neo-input" value={form.length} onChange={onF('length')} /></label>
                <label>Width (ft)<input type="number" className="neo-input" value={form.width} onChange={onF('width')} /></label>
                <label>Height (ft)<input type="number" className="neo-input" value={form.height} onChange={onF('height')} /></label>
              </div>
              <div className="actions whcfg-card-actions">
                <button className="btn" onClick={saveCreate}>Save</button>
                <button className="btn" onClick={cancelCreate}>Cancel</button>
              </div>
            </div>
          )}
          <div className="table-shell whcfg-table-wrap">
            <table className="wh-table whcfg-table" ref={whcTableRef}>
              <colgroup>
                {WHCFG_COLS.map((k, idx) => (
                  <col key={k} style={{ width: widthFor(k, idx === WHCFG_COLS.length - 1) }} />
                ))}
              </colgroup>
              <thead>
                <tr ref={whcHeaderRef}>
                  {WHCFG_COLS.map((col, idx) => (
                    <th key={col}>
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                      {idx !== WHCFG_COLS.length - 1 && (
                        <span
                          className="col-resizer"
                          onMouseDown={(e) => startWhcResize(col, e)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            autoFitWhcCol(col);
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10}>Loading…</td></tr>
                ) : rows.length ? rows.map(r => (
                  <tr key={r.id} className={selectedWh?.id === r.id ? 'selected' : ''} onClick={() => setSelectedWh(r)}>
                    <td className="whcfg-row-actions">
                      {editingId === r.id ? (
                        <>
                          <button className="mini-btn" title="Save" onClick={(e) => {
                            e.stopPropagation();
                            saveEdit(r.id);
                          }}>
                            <FaSave size={16} />
                          </button>
                          <button className="mini-btn" title="Cancel" onClick={(e) => {
                            e.stopPropagation();
                            cancelEdit();
                          }}>
                            <FaTimes size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="mini-btn" title="Edit" onClick={(e) => {
                            e.stopPropagation();
                            startEdit(r);
                          }}>
                            <FaEdit size={16} />
                          </button>
                          <button className="mini-btn danger" title="Delete" onClick={(e) => {
                            e.stopPropagation();
                            softDelete(r.id);
                          }}>
                            <FaTrash size={16} />
                          </button>
                        </>
                      )}
                    </td>
                    <td>{r.id}</td>
                    <td>{editingId === r.id ? <input className="neo-input" value={form.name} onChange={onF('name')} /> : (r.name || '')}</td>
                    <td>{editingId === r.id ? <input className="neo-input" value={form.address} onChange={onF('address')} /> : (r.address || '')}</td>
                    <td>{editingId === r.id ? <input className="neo-input" value={form.city} onChange={onF('city')} /> : (r.city || '')}</td>
                    <td>{editingId === r.id ? <input className="neo-input" value={form.state} onChange={onF('state')} /> : (r.state || '')}</td>
                    <td>{editingId === r.id ? <input className="neo-input" value={form.zip} onChange={onF('zip')} /> : (r.zip || '')}</td>
                    <td>{editingId === r.id ? <input type="number" className="neo-input" value={form.length} onChange={onF('length')} /> : (r.length)}</td>
                    <td>{editingId === r.id ? <input type="number" className="neo-input" value={form.width} onChange={onF('width')} /> : (r.width)}</td>
                    <td>{editingId === r.id ? <input type="number" className="neo-input" value={form.height} onChange={onF('height')} /> : (r.height)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={10}>No warehouses found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="whcfg-bottom-pane">
          <div className="neo-card whcfg-aisles-card">
            <div className="whcfg-aisles-toolbar">
              <h3>Aisles</h3>
              <div className="aisles-actions">
                <button
                  className="btn"
                  onClick={openPrintModalForSelection}
                  disabled={!selectedLocIds.size}
                  title={selectedLocIds.size ? `Print ${selectedLocIds.size} label(s)` : 'Select one or more rows first'}
                >
                  <FaPrint style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Print Label(s)
                </button>
                <button
                  className="btn"
                  onClick={openTypeModal}
                  disabled={!selectedLocIds.size}
                  title={selectedLocIds.size ? `Change type for ${selectedLocIds.size} selected` : 'Select one or more rows first'}
                >
                  <FaTag style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Set Type
                </button>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>Area</span>
                  <select
                    className="neo-input"
                    value={areaId}
                    onChange={(e) => setAreaId(e.target.value)}
                    disabled={!selectedWh || !areas.length}
                  >
                    {areas.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>Aisle</span>
                  <select
                    className="neo-input"
                    value={selectedAisle}
                    onChange={(e) => setSelectedAisle(e.target.value)}
                    disabled={!selectedWh || !aisles.length}
                  >
                    {aisles.map(a => (
                      <option key={a.roww} value={String(a.roww)}>{a.roww}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="btn aisles-refresh"
                  onClick={refreshAisles}
                  disabled={!selectedWh || !areaId || aislesLoading}
                >
                  <FaSync style={aislesLoading ? { marginRight: 6, verticalAlign: '-2px', animation: 'spin 1s linear infinite' } : { marginRight: 6, verticalAlign: '-2px' }} />
                  {aislesLoading ? 'Loading…' : 'Refresh'}
                </button>
                <button
                  className="btn primary"
                  onClick={() => {
                    setAisleForm(f => ({ ...f, aisle: String(nextAisle) }));
                    setNewAisleOpen(true);
                  }}
                  disabled={!selectedWh || !areaId}
                >
                  <FaPlus style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  New Aisle
                </button>
                <button
                  className="btn"
                  onClick={openNewBayModal}
                  disabled={!selectedWh || !areaId || !selectedAisle}
                >
                  <FaPlus style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  New Bay
                </button>
                <button
                  className="btn danger"
                  onClick={() => {
                    const ids = filteredLocs.filter(r => selectedLocIds.has(getLocKey(r)) && r.locationid).map(r => r.locationid);
                    setDeleteTargets(ids);
                    setDeleteModalOpen(true);
                  }}
                  disabled={!selectedWh || !areaId || !selectedAisle || !selectedLocIds.size}
                  title={selectedLocIds.size ? 'Delete selected empty location(s)' : 'Select location(s) first'}
                >
                  <FaTrash style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Delete
                </button>
              </div>
            </div>
            {!selectedWh ? (
              <div style={{ opacity: 0.8 }}>Select a warehouse above to manage its aisles.</div>
            ) : (
              <div className="whcfg-aisles-grid">
                <div className="whcfg-aisles-right">
                  <div className="aisles-list table-shell" ref={aislesListRef}>
                    <table className="whcfg-table whcfg-aisles-table">
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}>
                            <input
                              ref={selectAllRef}
                              type="checkbox"
                              checked={allSelected}
                              onChange={onToggleSelectAll}
                              disabled={!filteredLocs.length}
                              aria-label="Select all"
                            />
                          </th>
                          <th style={{ width: 90 }}>Actions</th>
                          <th style={{ width: 100 }}>Aisle</th>
                          <th style={{ width: 100 }}>Bay</th>
                          <th style={{ width: 100 }}>Shelf</th>
                          <th style={{ width: 100 }}>Slot/Bin</th>
                          <th style={{ width: 80 }}>Stack</th>
                          <th style={{ width: 160 }} ref={locTypeThRef}>Location Type</th>
                          <th style={{ width: 200 }}>SKU / Qty</th>
                          <th style={{ width: 160 }}>Storage Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locsLoading ? (
                          <tr><td colSpan={10}>Loading…</td></tr>
                        ) : filteredLocs.length ? (
                          filteredLocs.map((r, i) => (
                            <tr
                              key={getLocKey(r)}
                              className={selectedLocIds.has(getLocKey(r)) ? 'active' : ''}
                              onClick={(e) => onLocRowClick(r, i, e)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedLocIds.has(getLocKey(r))}
                                  onChange={(e) => onRowCheckboxChange(r, e)}
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label="Select row"
                                />
                              </td>
                              <td>
                                <button
                                  className="mini-btn"
                                  title="Print 4x2 Location Label"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    printLocLabel(r);
                                  }}
                                >
                                  <FaBarcode size={14} />
                                </button>
                              </td>
                              <td>{r.roww}</td>
                              <td>{r.columnn}</td>
                              <td>{r.shelve}</td>
                              <td>{r.bin}</td>
                              <td>{locStacksMap[r.locationid] || ''}</td>
                              <td>
                                {inlineTypeEditing === r.locationid ? (
                                  <select
                                    className="neo-input"
                                    value={inlineTypeValue}
                                    onChange={(e) => saveInlineType(r, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {(locationTypes || []).filter(t => String(t.id) !== '1').map(t => (
                                      <option key={t.id} value={String(t.id)}>{t.type || `Type ${t.id}`}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span
                                    title="Click to change type"
                                    style={{ textDecoration: 'underline dotted' }}
                                    onClick={(e) => { e.stopPropagation(); setInlineTypeEditing(r.locationid); setInlineTypeValue(String(r.type || '2')); }}
                                  >
                                    {typeLabelFor(r.type)}
                                  </span>
                                )}
                              </td>
                              <td>{locSkuQtyMap[r.locationid] || ''}</td>
                              <td>
                                {locStorageIdMap[r.locationid] ? (
                                  inlineSUEditing === r.locationid ? (
                                    <select
                                      className="neo-input"
                                      value={inlineSUValue || String(locStorageIdMap[r.locationid])}
                                      onChange={(e) => saveInlineStorageUnit(r, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {storageUnits.map(su => (
                                        <option key={su.id} value={String(su.id)}>{su.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span
                                      title="Click to change storage type"
                                      style={{ textDecoration: 'underline dotted' }}
                                      onClick={(e) => { e.stopPropagation(); setInlineSUEditing(r.locationid); setInlineSUValue(String(locStorageIdMap[r.locationid] || '')); }}
                                    >
                                      {locStorageMap[r.locationid] || ''}
                                    </span>
                                  )
                                ) : (
                                  ''
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={10}>No locations found for this aisle.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Split>
      <div className="whcfg-modals">
        {newAisleOpen && (
          <div className="whcfg-modal-overlay" onClick={() => setNewAisleOpen(false)}>
            <div className="whcfg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="whcfg-modal-head">
                <h3 style={{ margin: 0 }}>New Aisle</h3>
                <button className="mini-btn" onClick={() => setNewAisleOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="whcfg-modal-content">
                <div className="form-grid whcfg-aisle-form">
                  <label>Aisle #
                    <input
                      className="neo-input"
                      type="number"
                      min="1"
                      value={aisleForm.aisle || String(nextAisle)}
                      onChange={onAisleF('aisle')}
                    />
                  </label>
                  <label># Bays (columns)
                    <input className="neo-input" type="number" min="1" value={aisleForm.bays} onChange={onAisleF('bays')} />
                  </label>
                  <label># Shelves per bay
                    <input className="neo-input" type="number" min="1" value={aisleForm.shelves} onChange={onAisleF('shelves')} />
                  </label>
                  <label># Slots per shelf
                    <input className="neo-input" type="number" min="1" value={aisleForm.slots} onChange={onAisleF('slots')} />
                  </label>
                  <label>Location Type
                    <select className="neo-input" value={aisleForm.type} onChange={onAisleF('type')}>
                      {(locationTypes || []).filter(t => String(t.id) !== '1').map(t => (
                        <option key={t.id} value={String(t.id)}>{t.type || `Type ${t.id}`}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="whcfg-modal-actions">
                <button className="btn" onClick={() => setNewAisleOpen(false)}>Cancel</button>
                <button
                  className="btn primary"
                  disabled={!selectedWh || !areaId || creatingAisle}
                  onClick={async () => {
                    const ok = await createAisle();
                    if (ok) setNewAisleOpen(false);
                  }}
                >
                  {creatingAisle ? 'Creating…' : 'Create Aisle'}
                </button>
              </div>
            </div>
          </div>
        )}
        {typeModalOpen && (
          <div className="whcfg-modal-overlay" onClick={() => setTypeModalOpen(false)}>
            <div className="whcfg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="whcfg-modal-head">
                <h3 style={{ margin: 0 }}>Set Location Type</h3>
                <button className="mini-btn" onClick={() => setTypeModalOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="whcfg-modal-content">
                <div className="form-grid" style={{ gridTemplateColumns: 'minmax(220px, 1fr)' }}>
                  <label>Location Type
                    <select className="neo-input" value={pendingType} onChange={(e) => setPendingType(e.target.value)}>
                      {(locationTypes || []).filter(t => String(t.id) !== '1').map(t => (
                        <option key={t.id} value={String(t.id)}>{t.type || `Type ${t.id}`}</option>
                      ))}
                    </select>
                  </label>
                  <div style={{ opacity: 0.8 }}>
                    Applying to {selectedLocIds.size} selected {selectedLocIds.size === 1 ? 'row' : 'rows'}.
                  </div>
                </div>
              </div>
              <div className="whcfg-modal-actions">
                <button className="btn" onClick={() => setTypeModalOpen(false)} disabled={typeSaving}>Cancel</button>
                <button
                  className="btn primary"
                  onClick={saveSelectedTypes}
                  disabled={typeSaving || !selectedLocIds.size}
                >
                  {typeSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
        {printModalOpen && (
          <div className="whcfg-modal-overlay" onClick={() => setPrintModalOpen(false)}>
            <div className="whcfg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="whcfg-modal-head">
                <h3 style={{ margin: 0 }}>Print Location Label(s)</h3>
                <button className="mini-btn" onClick={() => setPrintModalOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="whcfg-modal-content">
                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))' }}>
                  <label className="span-2">Label Size</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="radio"
                        name="labelSize"
                        value="4x2"
                        checked={labelSize === '4x2'}
                        onChange={(e) => setLabelSize(e.target.value)}
                      />
                      4x2 in
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="radio"
                        name="labelSize"
                        value="4x1"
                        checked={labelSize === '4x1'}
                        onChange={(e) => setLabelSize(e.target.value)}
                      />
                      4x1 in
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="radio"
                        name="labelSize"
                        value="4x6"
                        checked={labelSize === '4x6'}
                        onChange={(e) => setLabelSize(e.target.value)}
                      />
                      4x6 in
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="radio"
                        name="labelSize"
                        value="ol125wx"
                        checked={labelSize === 'ol125wx'}
                        onChange={(e) => setLabelSize(e.target.value)}
                      />
                      OL125WX Template
                    </label>
                  </div>
                  <div className="span-2" style={{ opacity: 0.8 }}>
                    {labelSize === 'ol125wx' ? (
                      <>
                        Will print {printTargets.length} label{printTargets.length === 1 ? '' : 's'} across {ol125Pages} page{ol125Pages === 1 ? '' : 's'} (OL125WX: 10 labels per page).
                      </>
                    ) : (
                      <>Will print {printTargets.length} label{printTargets.length === 1 ? '' : 's'}.</>
                    )}
                  </div>
                </div>
              </div>
              <div className="whcfg-modal-actions">
                <button className="btn" onClick={() => setPrintModalOpen(false)}>Cancel</button>
                <button className="btn primary" onClick={doPrint}>Print</button>
              </div>
            </div>
          </div>
        )}
        {newBayOpen && (
          <div className="whcfg-modal-overlay" onClick={() => setNewBayOpen(false)}>
            <div className="whcfg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="whcfg-modal-head">
                <h3 style={{ margin: 0 }}>New Bay</h3>
                <button className="mini-btn" onClick={() => setNewBayOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="whcfg-modal-content">
                <div className="form-grid whcfg-aisle-form">
                  <label>Aisle #
                    <input className="neo-input" value={selectedAisle || ''} readOnly />
                  </label>
                  <label>Bay # (next)
                    <input className="neo-input" type="number" min="1" value={bayForm.bay} onChange={onBayF('bay')} />
                  </label>
                  <label># Shelves
                    <input className="neo-input" type="number" min="1" value={bayForm.shelves} onChange={onBayF('shelves')} />
                  </label>
                  <label># Slots per shelf
                    <input className="neo-input" type="number" min="1" value={bayForm.slots} onChange={onBayF('slots')} />
                  </label>
                  <label>Location Type
                    <select className="neo-input" value={bayForm.type} onChange={onBayF('type')}>
                      {(locationTypes || []).filter(t => String(t.id) !== '1').map(t => (
                        <option key={t.id} value={String(t.id)}>{t.type || `Type ${t.id}`}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="whcfg-modal-actions">
                <button className="btn" onClick={() => setNewBayOpen(false)}>Cancel</button>
                <button className="btn primary" onClick={createBay}>Create Bay</button>
              </div>
            </div>
          </div>
        )}
        {/* Delete confirmation modal */}
        {deleteModalOpen && (
          <div className="whcfg-modal-overlay" onClick={() => { if (!deleting) setDeleteModalOpen(false); }}>
            <div className="whcfg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="whcfg-modal-head">
                <h3 style={{ margin: 0 }}>Delete Locations</h3>
                <button className="mini-btn" onClick={() => { if (!deleting) setDeleteModalOpen(false); }} aria-label="Close">×</button>
              </div>
              <div className="whcfg-modal-content">
                {(() => {
                  const ids = deleteTargets || [];
                  const blocked = ids.filter(id => locHasInvMap[id]);
                  if (!ids.length) return (<div>No locations selected.</div>);
                  if (blocked.length) {
                    return (
                      <div>
                        <p style={{ color: '#c0392b', fontWeight: 600 }}>Some selected locations contain inventory.</p>
                        <p>Please uncheck those rows before deleting. Affected count: {blocked.length}.</p>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <p>Are you sure you want to delete {ids.length} location{ids.length===1?'':'s'}?</p>
                      <p style={{ opacity: 0.8 }}>Only empty locations can be deleted. This action cannot be undone.</p>
                    </div>
                  );
                })()}
              </div>
              <div className="whcfg-modal-actions">
                <button className="btn" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>Cancel</button>
                {(() => {
                  const ids = deleteTargets || [];
                  const blocked = ids.filter(id => locHasInvMap[id]);
                  return (
                    <button className="btn danger" onClick={async () => {
                      setDeleting(true);
                      try {
                        let ok = 0, fail = 0;
                        for (const id of ids) {
                          try {
                            const res = await fetch(`${API}/api/locations/${encodeURIComponent(id)}`, { method: 'DELETE' });
                            const j = await res.json().catch(() => ({}));
                            if (!res.ok || j?.error) { fail++; continue; }
                            ok++;
                          } catch { fail++; }
                        }
                        if (ok) refreshAisles();
                        if (fail) alert(`${fail} location(s) could not be deleted (not empty or error).`);
                        setDeleteModalOpen(false);
                      } finally {
                        setDeleting(false);
                      }
                    }} disabled={deleting || blocked.length>0}>
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
